import React, { useEffect, useMemo, useState } from 'react';
import { BodyContainer, ContentSettingsLayout } from './components/content-settings-layout';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { DetailViewHeaderProps } from '@web/components/detail-view';
import { EditModalValue, GenericContentManager } from './components/common/generic-content';
import { Techline } from '@packages/models/api';
import { useContentSettings } from '@packages/contexts/content-settings/content-settings.hooks';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ToastNotification from '@web/components/toast-notification';
import { StyleSheet } from 'react-native';
import colors from '@packages/core/styles/colors';
import { getLogger } from '@packages/core/logging';
import { isDevEnvironment } from '@web/utils/is-dev-environment';

const logger = getLogger('content-settings');

const contentAddMessage = 'Your content changes were published';
const contentDeletedMessage = 'Content deleted';
const TECHLINE_BANNER_TITLE_MAX_LENGTH = 80;
const TECHLINE_BANNER_DESCRIPTION_MAX_LENGTH = 240;

const styles = StyleSheet.create({
    button: {
        borderColor: colors.blueOne,
        borderWidth: 1,
    },
});

export const ContentSettingsTechline: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { state, loadDashboards, addDashboard, updateDashboard, deleteDashboard } = useContentSettings();
    const [showToast, setShowToast] = useState<{ show: boolean; message?: string }>({ show: false, message: '' });

    const headerProps: DetailViewHeaderProps = useMemo(
        () => ({
            breadcrumbs: [
                { to: '/administration', title: t('administration:breadcrumbs.adminHub', 'Administration Hub') },
                {
                    to: '/administration/content-settings',
                    title: t('administration:breadcrumbs.contentSettings', 'Content Settings'),
                },
                {
                    to: '/administration/content-settings/techline',
                    title: t('administration:breadcrumbs.edit', 'Edit'),
                },
            ],
            subtitle: t('administration:techline.subtitle', 'Techline'),
            rightSideItems: [
                <Button key="save" variant="ghost-blue" style={styles.button} onPress={() => navigate(-1)}>
                    {t('administration:techline.exit', 'Exit')}
                </Button>,
            ],
            hasBack: true,
        }),
        [t, navigate]
    );

    useEffect(() => {
        loadDashboards();
    }, []);

    const techlineContents =
        state.dashboardData?.techline?.map((techline) => ({
            buttonText: techline?.buttonText,
            description: techline.description,
            headingText: techline.title,
            id: techline.techlineDashboardId,
            link: techline.link,
            contentType: techline.contentType,
        })) || [];

    const findExistingTechline = (id?: string) =>
        state.dashboardData?.techline?.find((techline) => techline.techlineDashboardId === id);

    const mapToTechlinePayload = (techline: EditModalValue, overrides: Partial<Techline> = {}) => {
        const existing = findExistingTechline(techline.id);

        return {
            title: overrides.title ?? techline.title ?? existing?.title ?? '',
            description: overrides.description ?? techline.description ?? existing?.description ?? '',
            link: overrides.link ?? techline.link ?? existing?.link ?? '',
            buttonText: overrides.buttonText ?? techline.buttonText ?? existing?.buttonText ?? '',
            contentType: overrides.contentType ?? techline.contentType ?? existing?.contentType,
        } as Partial<Techline>;
    };

    const createOrUpdate = async (techline: EditModalValue, overrides: Partial<Techline> = {}) => {
        const payload = mapToTechlinePayload(techline, overrides);

        if (!payload.contentType && overrides.contentType) {
            payload.contentType = overrides.contentType;
        }

        if (techline.id) {
            await updateDashboard('techline', techline.id, payload);
        } else {
            await addDashboard('techline', payload as unknown as Techline);
        }

        setShowToast({ show: true, message: contentAddMessage });
        return techline;
    };

    const deleteBanner = async (id: string) => {
        try {
            await deleteDashboard('techline', id);
            setShowToast({ show: true, message: contentDeletedMessage });
        } catch (error) {
            logger.error(`Failed to delete the techline banner [${id}]`, { error, techlineDashboardId: id });
        }
    };

    const categorizedContent = {
        BANNER: techlineContents.filter(({ contentType }) => contentType === 'BANNER' || !contentType),
        VIDEO: techlineContents.filter(({ contentType }) => contentType === 'VIDEO'),
        QUICK_TIP: techlineContents.filter(({ contentType }) => contentType === 'QUICK_TIP'),
    };
    return (
        <ContentSettingsLayout
            headerProps={headerProps}
            message="These messages will appear to all users that visit the techline page."
        >
            <ToastNotification
                show={showToast.show}
                onClose={() => setShowToast({ ...showToast, show: false })}
                body={
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        <Icon name="checkmark" size={20} color="white" />
                        <Typography variant="h6" color="white">
                            {showToast.message}
                        </Typography>
                    </div>
                }
                autoHideTime={2000}
                variant="success"
                position="top-center"
            />

            <BodyContainer divider={true}>
                <GenericContentManager
                    genericContentProps={{
                        headerProps: {
                            title: 'Banners',
                            buttonLabel: 'Add Banner',
                        },
                        cardProps: {
                            title: 'Banner',
                            hideCopyButton: true,
                        },
                        modalProps: {
                            title: 'Banner',
                            submitButtonText: 'Save & Publish',
                            formFields: [
                                {
                                    type: 'TextInput',
                                    fieldName: 'title',
                                    required: true,
                                    contentFieldMapper: 'headingText',
                                    placeholder: 'Write title here',
                                    label: 'Title',
                                    maxLength: TECHLINE_BANNER_TITLE_MAX_LENGTH,
                                    showCharCount: true,
                                },
                                {
                                    type: 'TextInput',
                                    fieldName: 'description',
                                    contentFieldMapper: 'description',
                                    required: true,
                                    placeholder: 'Write copy here',
                                    label: 'Description',
                                    maxLength: TECHLINE_BANNER_DESCRIPTION_MAX_LENGTH,
                                    showCharCount: true,
                                },
                                {
                                    type: 'TextInput',
                                    fieldName: 'link',
                                    contentFieldMapper: 'link',
                                    placeholder: 'Write Edit here',
                                    label: 'Link',
                                    required: false,
                                },
                            ],
                        },
                        contents: categorizedContent['BANNER'],
                        saveCallback: async (techline) =>
                            createOrUpdate(techline, {
                                contentType: 'BANNER',
                                ...(techline.id ? {} : { buttonText: 'Read More' }),
                            }),
                        deleteCallBack: deleteBanner,
                        maxInstance: 1,
                        sortingEnabled: false,
                        confirmDeleteModal: true,
                    }}
                />
            </BodyContainer>

            <BodyContainer divider={true}>
                <GenericContentManager
                    genericContentProps={{
                        headerProps: {
                            title: 'Quick tips',
                            buttonLabel: 'Add Quick Tip',
                            hideButton: categorizedContent['QUICK_TIP'].length > 0,
                        },
                        cardProps: { title: 'Quick tip', hideCopyButton: true },
                        modalProps: {
                            title: 'Quick Tips',
                            formFields: [
                                {
                                    type: 'TextInput',
                                    fieldName: 'description',
                                    contentFieldMapper: 'description',
                                    required: true,
                                    placeholder: 'Write description here',
                                    label: 'Description',
                                },
                                {
                                    type: 'TextInput',
                                    fieldName: 'link',
                                    contentFieldMapper: 'link',
                                    placeholder: 'Enter TIPS locator link here',
                                    label: 'Enter TIPS locator link',
                                    required: false,
                                },
                            ],
                        },
                        contents: categorizedContent['QUICK_TIP'],
                        saveCallback: async (techline) =>
                            createOrUpdate(techline, {
                                title: 'Quick tip',
                                contentType: 'QUICK_TIP',
                                buttonText: 'View TIPS locator',
                            }),
                        maxInstance: 1,
                        sortingEnabled: false,
                    }}
                />
            </BodyContainer>

            <BodyContainer>
                <GenericContentManager
                    genericContentProps={{
                        headerProps: {
                            title: 'How-To Videos',
                            buttonLabel: "Add How To's",
                            hideButton: categorizedContent['VIDEO'].length > 0,
                        },
                        cardProps: { title: 'How-To Videos', hideCopyButton: true },
                        modalProps: {
                            title: 'How-To Videos',
                            formFields: [
                                {
                                    type: 'TextInput',
                                    fieldName: 'description',
                                    contentFieldMapper: 'description',
                                    required: true,
                                    placeholder: 'Write description here',
                                    label: 'Description',
                                },
                                {
                                    type: 'TextInput',
                                    fieldName: 'link',
                                    contentFieldMapper: 'link',
                                    placeholder: 'Enter how-to video link here',
                                    label: 'Enter how-to video link',
                                    required: false,
                                },
                            ],
                        },
                        contents: categorizedContent['VIDEO'],
                        saveCallback: async (techline) =>
                            createOrUpdate(techline, {
                                title: 'Techline How To’s',
                                contentType: 'VIDEO',
                                buttonText: 'View',
                            }),
                        maxInstance: 1,
                        sortingEnabled: false,
                    }}
                />
            </BodyContainer>
        </ContentSettingsLayout>
    );
};
